import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { faqs } from '@/mocks/comics';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const pageUrl = 'http://heniukeji.com/about';

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们', href: undefined },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://heniukeji.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: pageUrl },
    ],
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, String(value));
      });

      const response = await fetch('http://heniukeji.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormError(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 哈哈漫画网',
    description: '哈哈漫画网是专业的韩国漫画免费在线阅读平台，提供高清韩漫全集下拉式阅读和漫画大全。',
    url: pageUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: '哈哈漫画网',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const milestones = [
    { year: '2022', title: '平台创立', desc: '哈哈漫画网正式上线，专注于韩国漫画内容聚合与传播' },
    { year: '2023', title: '快速发展', desc: '漫画资源突破500部，月活跃读者突破10万人' },
    { year: '2024', title: '体验升级', desc: '推出高清下拉式阅读功能，全面优化移动端阅读体验' },
    { year: '2025', title: '行业领先', desc: '漫画资源突破1000部，成为韩国漫画领域的标杆平台' },
    { year: '2026', title: '持续创新', desc: '引入智能推荐系统，实现千人千面的个性化阅读推荐' },
  ];

  return (
    <>
      <SEOHead
        title="关于我们 - 品牌介绍 - 哈哈漫画网"
        description="哈哈漫画网是专业的韩国漫画免费在线阅读平台，提供高清韩漫全集下拉式阅读和漫画大全，致力于为广大漫画爱好者提供优质的阅读服务。"
        keywords="关于我们, 哈哈漫画网, 韩国漫画, 免费在线观看, 品牌介绍"
        canonicalUrl={pageUrl}
        ogTitle="关于我们 - 哈哈漫画网"
        ogDescription="哈哈漫画网是专业的韩国漫画免费在线阅读平台，提供高清韩漫全集下拉式阅读和漫画大全。"
        schemaJson={[aboutSchema, faqSchema, breadcrumbSchema]}
      />

      <section className="relative h-[280px] md:h-[360px] w-full overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20watercolor%20illustration%20of%20a%20cozy%20reading%20nook%20with%20bookshelves%20and%20warm%20lighting%2C%20soft%20cream%20and%20warm%20beige%20gradient%20background%2C%20peaceful%20atmospheric%20mood%2C%20minimalist%20style%2C%20no%20text&width=1920&height=600&seq=about-hero&orientation=landscape"
          alt="关于哈哈漫画网"
          title="哈哈漫画网 - 关于我们"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
        <Navbar />
        <div className="relative z-10 w-full h-full flex items-end pb-10 md:pb-14 px-4 md:px-6">
          <div className="w-full max-w-2xl">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50">
              关于我们
            </h1>
            <p className="text-sm md:text-base text-background-50/80 mt-2 max-w-lg">
              了解哈哈漫画网的品牌故事与发展历程
            </p>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <span className="text-xs font-medium text-foreground-400 uppercase tracking-wider">品牌介绍</span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mt-2 leading-tight">
                让每一位读者都能
                <span className="block italic text-primary-500 font-serif">免费畅享</span>
                优质韩漫
              </h2>
              <p className="mt-3 text-sm text-foreground-500 max-w-md">
                哈哈漫画网成立于2022年，专注韩国漫画领域
              </p>

              <div className="grid grid-cols-2 gap-4 mt-8">
                <div className="bg-primary-100/70 rounded-2xl p-5 md:p-6 relative overflow-hidden">
                  <div className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-background-50/80">
                    <i className="ri-arrow-right-up-line text-primary-500 text-sm"></i>
                  </div>
                  <p className="text-3xl md:text-4xl font-heading font-bold text-primary-600">10w+</p>
                  <p className="text-sm text-foreground-700 mt-1">每日活跃读者</p>
                  <p className="text-xs text-foreground-500 mt-2">每天都在增长中</p>
                  <p className="text-xs italic text-primary-400 mt-3">让阅读成为习惯</p>
                </div>

                <div className="bg-secondary-100/70 rounded-2xl p-5 md:p-6 relative overflow-hidden">
                  <div className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-background-50/80">
                    <i className="ri-arrow-right-up-line text-secondary-500 text-sm"></i>
                  </div>
                  <p className="text-3xl md:text-4xl font-heading font-bold text-secondary-600">1000+</p>
                  <p className="text-sm text-foreground-700 mt-1">漫画资源数量</p>
                  <p className="text-xs text-foreground-500 mt-2">每日持续更新中</p>
                  <p className="text-xs italic text-secondary-400 mt-3">海量内容随心看</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20watercolor%20illustration%20of%20a%20modern%20creative%20workspace%20with%20large%20windows%20and%20natural%20light%2C%20people%20collaborating%20around%20a%20table%20with%20sketches%20and%20digital%20tablets%2C%20warm%20beige%20and%20soft%20teal%20color%20palette%2C%20artistic%20atmosphere%2C%20no%20text&width=700&height=500&seq=about-team&orientation=landscape"
                alt="哈哈漫画网团队"
                title="哈哈漫画网 - 团队文化"
                className="rounded-2xl w-full object-cover"
              />
              <div className="absolute bottom-4 left-4 bg-background-50/90 backdrop-blur-sm rounded-xl px-4 py-2">
                <p className="text-xs font-semibold text-foreground-800">核心团队</p>
                <p className="text-xs text-foreground-500">来自互联网与内容行业</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 mb-3">
              <i className="ri-timeline-view text-sm"></i>
              发展历程
            </span>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
              我们的 <span className="text-accent-500">成长之路</span>
            </h2>
          </div>

          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-accent-200 md:-translate-x-px"></div>
            <div className="flex flex-col gap-8 md:gap-12">
              {milestones.map((item, idx) => (
                <div
                  key={item.year}
                  className={`relative flex items-start gap-6 md:gap-10 ${
                    idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-12 md:pl-0`}>
                    <div className={`md:px-8 ${idx % 2 === 0 ? 'md:pr-0 md:text-right' : 'md:pl-0'}`}>
                      <span className="inline-block text-lg md:text-xl font-heading font-bold text-primary-500">
                        {item.year}
                      </span>
                      <h3 className="text-base md:text-lg font-semibold text-foreground-900 mt-1">{item.title}</h3>
                      <p className="text-sm text-foreground-500 mt-1">{item.desc}</p>
                    </div>
                  </div>

                  <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50 md:-translate-x-1.5 z-10 mt-1.5"></div>

                  <div className="flex-1 hidden md:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 mb-3">
              <i className="ri-shield-check-line text-sm"></i>
              品牌信誉
            </span>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
              值得信赖的 <span className="text-accent-500">漫画平台</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {[
              {
                icon: 'ri-shield-check-line',
                title: '正版授权',
                desc: '所有漫画内容均获得正版授权，尊重知识产权，为创作者和读者搭建健康的阅读桥梁。',
                color: 'bg-accent-100',
                iconColor: 'text-accent-600',
              },
              {
                icon: 'ri-speed-up-line',
                title: '极速加载',
                desc: '采用先进的CDN加速技术，确保漫画页面快速加载，流畅的下拉式阅读体验零等待。',
                color: 'bg-primary-100',
                iconColor: 'text-primary-600',
              },
              {
                icon: 'ri-user-heart-line',
                title: '用户至上',
                desc: '以读者体验为核心，持续优化产品功能，48小时内响应用户反馈，用心服务每一位漫迷。',
                color: 'bg-secondary-100',
                iconColor: 'text-secondary-600',
              },
            ].map((item) => (
              <div key={item.title} className={`${item.color} rounded-2xl p-6 md:p-7`}>
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-50 mb-4">
                  <i className={`${item.icon} ${item.iconColor} text-lg`}></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-900 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 mb-3">
              <i className="ri-question-answer-line text-sm"></i>
              常见问题
            </span>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
              您可能想知道
            </h2>
          </div>

          <div className="flex flex-col gap-3">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="bg-background-100 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                >
                  <span className="text-sm md:text-base font-semibold text-foreground-900 pr-4">
                    {faq.question}
                  </span>
                  <i
                    className={`text-foreground-400 text-sm transition-transform duration-300 flex-shrink-0 ${
                      openFaq === idx ? 'ri-subtract-line rotate-180' : 'ri-add-line'
                    }`}
                  ></i>
                </button>
                {openFaq === idx && (
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 mb-3">
              <i className="ri-mail-send-line text-sm"></i>
              联系我们
            </span>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
              期待与您 <span className="text-accent-500">沟通交流</span>
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              如有任何问题或建议，欢迎随时联系我们
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {[
              {
                icon: 'ri-mail-line',
                title: '电子邮箱',
                content: 'hello@heniukeji.com',
                desc: '工作日24小时内回复',
              },
              {
                icon: 'ri-wechat-line',
                title: '微信公众号',
                content: '哈哈漫画网',
                desc: '关注获取最新资讯',
              },
              {
                icon: 'ri-map-pin-line',
                title: '公司地址',
                content: '中国·北京',
                desc: '朝阳区互联网产业园',
              },
            ].map((item) => (
              <div key={item.title} className="bg-background-50 rounded-2xl p-5 md:p-6 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100">
                  <i className={`${item.icon} text-primary-500 text-lg`}></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                <p className="text-sm text-foreground-700 mt-1 font-medium">{item.content}</p>
                <p className="text-xs text-foreground-500 mt-1">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 rounded-2xl bg-background-50">
            <form
              id="contact-form"
             
              className="flex flex-col gap-4"
              onSubmit={handleFormSubmit}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="name"
                  placeholder="您的姓名"
                  className="px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  required
                />
                <input
                  type="email"
                  name="email"
                  placeholder="您的邮箱"
                  className="px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  required
                />
              </div>
              <textarea
                name="message"
                placeholder="请输入您的留言内容..."
                rows={4}
                maxLength={500}
                className="px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                required
              ></textarea>
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-honeypot"
              />
              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? (
                  <>提交中...</>
                ) : (
                  <>发送留言 <i className="ri-send-plane-line"></i></>
                )}
              </button>
              {formStatus === 'success' && (
                <p className="text-sm text-accent-600 text-center">感谢您的留言，我们会尽快回复！</p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-red-500 text-center">{formError}</p>
              )}
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}